/**
 * "Hitta på LinkedIn" + URL-fält. När en giltig LinkedIn-adress klistras in
 * hämtas data direkt (med nya försök) och lämnas till `onHamtad`, som sparar.
 * Ingen separat sparning behövs.
 *
 * typ: 'company' | 'person'
 * onHamtad(mapped, url): async – sparar och uppdaterar state hos anroparen.
 */
import React, { useRef, useState } from 'react';
import { Box, Button, TextField, CircularProgress, Typography, Tooltip, InputAdornment } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { enrichCompany, enrichPerson, mapCompanyToForm, mapPersonToContact } from '../../services/enrichLayer';
import { giltigLinkedinUrl, linkedinForetagSokUrl, linkedinPersonSokUrl } from './bokningsmail';

export const LINKEDIN_MAX_FORSOK = 3;
const vanta = ms => new Promise(r => setTimeout(r, ms));

/** Hämtar och mappar, med backoff 1 s, 2 s mellan försöken. 404 provas inte om. */
export async function hamtaLinkedIn(typ, url, onForsok) {
  let senasteFel;
  for (let forsok = 1; forsok <= LINKEDIN_MAX_FORSOK; forsok++) {
    onForsok?.(forsok);
    try {
      const data = typ === 'company' ? await enrichCompany(url) : await enrichPerson(url);
      return typ === 'company' ? mapCompanyToForm(data) : mapPersonToContact(data);
    } catch (e) {
      senasteFel = e;
      if (/hittades inte/i.test(e.message || '')) break;
      if (forsok < LINKEDIN_MAX_FORSOK) await vanta(1000 * 2 ** (forsok - 1));
    }
  }
  throw senasteFel;
}

const tomt = v => v == null || v === '';
/** Fyller tomma fält, skriver aldrig över det någon redan skrivit in. */
function fyllTomma(befintlig, nytt, alltid) {
  const ut = {};
  for (const [k, v] of Object.entries(nytt)) {
    if (tomt(v)) continue;
    if (alltid.includes(k) || tomt(befintlig?.[k])) ut[k] = v;
  }
  return ut;
}

/** Fält att spara på en kontakt efter LinkedIn-hämtning. */
export function kontaktFaltFranLinkedIn(kontakt, mapped, url) {
  return {
    ...fyllTomma(kontakt, mapped, ['photoUrl', 'linkedinHeadline', 'currentCompany', 'education',
      'languages', 'experience', 'certifications', 'skills']),
    linkedin: url,
  };
}

/** Fält att spara på en kund efter LinkedIn-hämtning. */
export function kundFaltFranLinkedIn(kund, mapped, url) {
  const { name, ...rest } = mapped; // namnet från LinkedIn ersätter inte det registrerade
  return { ...fyllTomma(kund, rest, ['logoUrl', 'specialties', 'companyType']), linkedin: url };
}

export default function LinkedInHamtning({ typ, namn, foretag, onHamtad, kompakt }) {
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState('idle'); // idle | laddar | klar | fel
  const [meddelande, setMeddelande] = useState('');
  const senast = useRef('');

  async function starta(adress) {
    const ren = adress.trim();
    if (!giltigLinkedinUrl(ren, typ) || ren === senast.current) return;
    senast.current = ren;
    setStatus('laddar');
    setMeddelande('Hämtar…');
    try {
      const mapped = await hamtaLinkedIn(typ, ren, f => f > 1 && setMeddelande(`Försök ${f} av ${LINKEDIN_MAX_FORSOK}…`));
      await onHamtad(mapped, ren);
      setStatus('klar');
      setMeddelande(`Hämtat${mapped.name ? `: ${mapped.name}` : ''}`);
    } catch (e) {
      senast.current = '';
      setStatus('fel');
      setMeddelande(e.message || 'Kunde inte hämta');
    }
  }

  const sokUrl = typ === 'company' ? linkedinForetagSokUrl(namn) : linkedinPersonSokUrl(namn, foretag);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: kompakt ? 0.5 : 1 }}>
      <Tooltip title="Saknar LinkedIn – sök och klistra in adressen">
        <Button size="small" variant="outlined" startIcon={<LinkedInIcon sx={{ fontSize: 14 }} />}
          onClick={e => { e.stopPropagation(); window.open(sokUrl, '_blank', 'noopener'); }}
          sx={{ fontSize: '0.68rem', py: 0, px: 1, minHeight: 24, borderColor: '#0A66C2', color: '#0A66C2', textTransform: 'none' }}>
          Hitta på LinkedIn
        </Button>
      </Tooltip>
      <TextField size="small" value={url}
        placeholder={typ === 'company' ? 'linkedin.com/company/…' : 'linkedin.com/in/…'}
        onClick={e => e.stopPropagation()}
        onChange={e => { setUrl(e.target.value); if (status === 'fel') setStatus('idle'); starta(e.target.value); }}
        disabled={status === 'laddar'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {status === 'laddar' ? <CircularProgress size={12} />
                : status === 'klar' ? <CheckCircleIcon sx={{ fontSize: 14, color: '#2E7D32' }} /> : null}
            </InputAdornment>
          ),
        }}
        sx={{ width: kompakt ? 200 : 240, '& .MuiInputBase-root': { fontSize: '0.72rem', height: 26 } }} />
      {meddelande && (
        <Typography variant="caption"
          sx={{ fontSize: '0.68rem', color: status === 'fel' ? '#C62828' : status === 'klar' ? '#2E7D32' : 'text.secondary' }}>
          {meddelande}
        </Typography>
      )}
    </Box>
  );
}
